import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { usePagination } from "@/lib/hooks/use-pagination";
import { useProducts, useSuitabilityOptions } from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { TableCardSkeleton } from "../../skeleton-loaders";
import { useProductsTabContext } from "../products-tab-context";
import { T } from "./page.i18n";

export default function ProductsSuitabilityPage() {
  const {
    collectionId,
    debouncedSearch,
    openEdit,
    openDelete,
    loadingProductId,
    checkingDeleteId,
  } = useProductsTabContext();
  const { products, isLoading: productsLoading } = useProducts(collectionId, debouncedSearch);
  const { suitabilityOptions, isLoading: optionsLoading } = useSuitabilityOptions(collectionId);
  const t = useT(T);
  const localize = useLocalize();
  const {
    page,
    setPage,
    pageSize,
    totalCount,
    totalPages,
    pageItems: paginatedProducts,
  } = usePagination(products, 10);

  if ((productsLoading && products.length === 0 && !debouncedSearch) || optionsLoading) {
    return <TableCardSkeleton columns={6} />;
  }

  if (products.length === 0 && debouncedSearch.trim() !== "") {
    return <EmptyState>{t.noProductsMatch}</EmptyState>;
  }

  if (products.length === 0) {
    return <EmptyState>{t.noProducts}</EmptyState>;
  }

  const productTypeName = (typeId: string | null) => {
    if (!typeId) return null;
    const found = suitabilityOptions.productTypes.find((p) => p.id === typeId);
    return found ? localize(found.nameI18n) : null;
  };

  const riskLevelLabel = (riskId: string | null) => {
    if (!riskId) return null;
    const found = suitabilityOptions.riskLevels.find((r) => r.id === riskId);
    return found ? localize(found.labelI18n) : null;
  };

  const timeHorizonLabel = (horizonId: string | null) => {
    if (!horizonId) return null;
    const found = suitabilityOptions.timeHorizons.find((h) => h.id === horizonId);
    return found ? localize(found.labelI18n) : null;
  };

  const renderCell = (label: string | null) =>
    label ? (
      <span className="text-sm">{label}</span>
    ) : (
      <span className="text-muted-foreground italic text-xs">{t.notSet}</span>
    );

  return (
    <>
      <Table variant="data">
        <TableHeader>
          <TableRow>
            <TableHead>{t.productName}</TableHead>
            {suitabilityOptions.productTypesEnabled && <TableHead>{t.productType}</TableHead>}
            <TableHead>{t.minRiskLevel}</TableHead>
            <TableHead>{t.minRiskScore}</TableHead>
            <TableHead>{t.minTimeHorizon}</TableHead>
            <TableActionsHead>{t.actions}</TableActionsHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {paginatedProducts.map((product) => (
            <TableRow key={product.id}>
              <TableCell className="font-medium">{localize(product.nameI18n)}</TableCell>
              {suitabilityOptions.productTypesEnabled && (
                <TableCell>{renderCell(productTypeName(product.productTypeId))}</TableCell>
              )}
              <TableCell>{renderCell(riskLevelLabel(product.minRiskLevelId))}</TableCell>
              {/* != null — 0 is a real score and must render as "0". */}
              <TableCell>
                {renderCell(product.minRiskScore != null ? String(product.minRiskScore) : null)}
              </TableCell>
              <TableCell>{renderCell(timeHorizonLabel(product.minTimeHorizonId))}</TableCell>
              <TableActionsCell>
                <Button
                  variant="ghost"
                  size="icon"
                  tooltip={t.edit}
                  onClick={() => openEdit(product.id)}
                  loading={loadingProductId === product.id}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  tooltip={t.delete}
                  loading={checkingDeleteId === product.id}
                  onClick={() => openDelete({ id: product.id, nameI18n: product.nameI18n })}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableActionsCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </>
  );
}
